
import { useState, useEffect } from 'react';
import { Product, ProductVariant } from '../page';

interface ProductFormProps {
  product?: Product;
  onSubmit: (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
  categories: string[];
}

export default function ProductForm({ product, onSubmit, onCancel, categories }: ProductFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    sizes: [] as string[],
    colors: [] as string[],
    variants: [] as ProductVariant[],
    productionPrice: 0,
    sellingPrice: 0,
    image: '',
    images: [] as string[],
    description: '',
    category: ''
  });

  const [newSize, setNewSize] = useState('');
  const [newColor, setNewColor] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [editingColorName, setEditingColorName] = useState<{ oldName: string; newName: string } | null>(null);
  const [loading, setLoading] = useState(false);
  
  // New states for image management
  const [newMainImage, setNewMainImage] = useState('');
  const [newVariantImage, setNewVariantImage] = useState('');
  const [selectedVariantForImage, setSelectedVariantForImage] = useState<{ size: string; color: string } | null>(null);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        sizes: [...product.sizes],
        colors: [...product.colors],
        variants: [...product.variants],
        productionPrice: product.productionPrice,
        sellingPrice: product.sellingPrice,
        image: product.image,
        images: [...(product.images || [product.image])],
        description: product.description || '',
        category: product.category || ''
      });
    }
  }, [product]);

  // Generate variants when sizes or colors change
  useEffect(() => {
    if (formData.sizes.length > 0 && formData.colors.length > 0) {
      const newVariants: ProductVariant[] = [];
      formData.sizes.forEach(size => {
        formData.colors.forEach(color => {
          const existingVariant = formData.variants.find(v => v.size === size && v.color === color);
          newVariants.push({
            size,
            color,
            quantity: existingVariant ? existingVariant.quantity : 0,
            images: existingVariant ? existingVariant.images : []
          });
        });
      });
      setFormData(prev => ({ ...prev, variants: newVariants }));
    }
  }, [formData.sizes, formData.colors]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Tên sản phẩm là bắt buộc';
    }

    if (formData.sizes.length === 0) {
      newErrors.sizes = 'Phải có ít nhất một size';
    }

    if (formData.colors.length === 0) {
      newErrors.colors = 'Phải có ít nhất một màu sắc';
    }

    if (formData.productionPrice <= 0) {
      newErrors.productionPrice = 'Giá sản xuất phải lớn hơn 0';
    }

    if (formData.sellingPrice <= 0) {
      newErrors.sellingPrice = 'Giá bán phải lớn hơn 0';
    }

    if (formData.sellingPrice <= formData.productionPrice) {
      newErrors.sellingPrice = 'Giá bán phải lớn hơn giá sản xuất';
    }

    if (!formData.image.trim() && formData.images.length === 0) {
      newErrors.image = 'Phải có ít nhất một ảnh sản phẩm';
    }

    if (!formData.category) {
      newErrors.category = 'Danh mục là bắt buộc';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        const finalFormData = {
          ...formData,
          image: formData.image || formData.images[0] || '',
          images: formData.images.length > 0 ? formData.images : [formData.image]
        };
        await onSubmit(finalFormData);
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const addSize = () => {
    if (newSize.trim() && !formData.sizes.includes(newSize.trim())) {
      setFormData({
        ...formData,
        sizes: [...formData.sizes, newSize.trim()]
      });
      setNewSize('');
    }
  };

  const removeSize = (size: string) => {
    setFormData({
      ...formData,
      sizes: formData.sizes.filter(s => s !== size),
      variants: formData.variants.filter(v => v.size !== size)
    });
  };

  const addColor = () => {
    if (newColor.trim() && !formData.colors.includes(newColor.trim())) {
      setFormData({
        ...formData,
        colors: [...formData.colors, newColor.trim()]
      });
      setNewColor('');
    }
  };

  const removeColor = (color: string) => {
    setFormData({
      ...formData,
      colors: formData.colors.filter(c => c !== color),
      variants: formData.variants.filter(v => v.color !== color)
    });
  };

  const updateColorName = (oldName: string, newName: string) => {
    if (newName.trim() && newName !== oldName) {
      setFormData({
        ...formData,
        colors: formData.colors.map(c => c === oldName ? newName.trim() : c),
        variants: formData.variants.map(v => 
          v.color === oldName ? { ...v, color: newName.trim() } : v
        )
      });
    }
    setEditingColorName(null);
  };

  const updateVariantQuantity = (size: string, color: string, quantity: number) => {
    setFormData({
      ...formData,
      variants: formData.variants.map(v => 
        v.size === size && v.color === color 
          ? { ...v, quantity: Math.max(0, quantity) }
          : v
      )
    });
  };

  // Main product images management
  const addMainImage = () => {
    if (newMainImage.trim() && !formData.images.includes(newMainImage.trim())) {
      const updatedImages = [...formData.images, newMainImage.trim()];
      setFormData({
        ...formData,
        images: updatedImages,
        image: formData.image || newMainImage.trim()
      });
      setNewMainImage('');
    }
  };

  const removeMainImage = (imageUrl: string) => {
    const updatedImages = formData.images.filter(img => img !== imageUrl);
    setFormData({
      ...formData,
      images: updatedImages,
      image: formData.image === imageUrl ? (updatedImages[0] || '') : formData.image
    });
  };

  const setAsMainImage = (imageUrl: string) => {
    setFormData({
      ...formData,
      image: imageUrl
    });
  };

  const generateMainImageUrl = () => {
    if (formData.name.trim()) {
      const prompt = `high quality product photography of ${formData.name} on clean white background, professional lighting, modern design, premium quality materials, e-commerce style product shot, pickleball equipment`;
      const imageUrl = `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28prompt%29%7D&width=400&height=400&seq=${Date.now()}&orientation=squarish`;
      setNewMainImage(imageUrl);
    }
  };

  // Variant images management
  const addVariantImage = (size: string, color: string) => {
    if (newVariantImage.trim()) {
      setFormData({
        ...formData,
        variants: formData.variants.map(v => 
          v.size === size && v.color === color 
            ? { ...v, images: [...(v.images || []), newVariantImage.trim()] }
            : v
        )
      });
      setNewVariantImage('');
      setSelectedVariantForImage(null);
    }
  };

  const removeVariantImage = (size: string, color: string, imageUrl: string) => {
    setFormData({
      ...formData,
      variants: formData.variants.map(v => 
        v.size === size && v.color === color 
          ? { ...v, images: (v.images || []).filter(img => img !== imageUrl) }
          : v
      )
    });
  };

  const generateVariantImage = (size: string, color: string) => {
    if (formData.name.trim()) {
      const prompt = `high quality product photography of ${formData.name} in ${color} color, size ${size}, on clean white background, professional lighting, modern design, premium quality materials, e-commerce style product shot, pickleball equipment`;
      const imageUrl = `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28prompt%29%7D&width=400&height=400&seq=${Date.now()}&orientation=squarish`;
      setNewVariantImage(imageUrl);
      setSelectedVariantForImage({ size, color });
    }
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh]">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {product ? 'Chỉnh sửa sản phẩm' : 'Thêm sản phẩm mới'}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {product ? 'Cập nhật thông tin sản phẩm Pickleball' : 'Nhập thông tin sản phẩm Pickleball mới'}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin cơ bản</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tên sản phẩm *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Nhập tên sản phẩm Pickleball"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Danh mục *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-8 ${
                        errors.category ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Chọn danh mục</option>
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                    {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mô tả sản phẩm
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Nhập mô tả sản phẩm Pickleball"
                      maxLength={500}
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 ký tự</p>
                  </div>
                </div>
              </div>

              {/* Sizes */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Size/Phiên bản *</h3>
                
                <div className="space-y-3">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newSize}
                      onChange={(e) => setNewSize(e.target.value)}
                      placeholder="Nhập size/phiên bản (VD: Standard, Lightweight, Pro...)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSize())}
                    />
                    <button
                      type="button"
                      onClick={addSize}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 whitespace-nowrap"
                    >
                      Thêm
                    </button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {formData.sizes.map(size => (
                      <span
                        key={size}
                        className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm"
                      >
                        {size}
                        <button
                          type="button"
                          onClick={() => removeSize(size)}
                          className="ml-2 text-orange-600 hover:text-orange-800"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                  {errors.sizes && <p className="text-red-500 text-sm">{errors.sizes}</p>}
                </div>
              </div>

              {/* Colors */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Màu sắc/Phiên bản màu *</h3>
                
                <div className="space-y-3">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newColor}
                      onChange={(e) => setNewColor(e.target.value)}
                      placeholder="Nhập tên màu tùy chỉnh (VD: Đỏ Racing, Xanh Ocean...)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addColor())}
                    />
                    <button
                      type="button"
                      onClick={addColor}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 whitespace-nowrap"
                    >
                      Thêm
                    </button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {formData.colors.map(color => (
                      <div key={color} className="inline-flex items-center">
                        {editingColorName?.oldName === color ? (
                          <div className="flex items-center space-x-1">
                            <input
                              type="text"
                              value={editingColorName.newName}
                              onChange={(e) => setEditingColorName({ ...editingColorName, newName: e.target.value })}
                              className="px-2 py-1 border border-gray-300 rounded text-sm w-32"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  updateColorName(editingColorName.oldName, editingColorName.newName);
                                }
                              }}
                              onBlur={() => updateColorName(editingColorName.oldName, editingColorName.newName)}
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => updateColorName(editingColorName.oldName, editingColorName.newName)}
                              className="text-green-600 hover:text-green-800 p-1"
                            >
                              <i className="ri-check-line text-xs"></i>
                            </button>
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                            {color}
                            <button
                              type="button"
                              onClick={() => setEditingColorName({ oldName: color, newName: color })}
                              className="ml-1 text-green-600 hover:text-green-800"
                            >
                              <i className="ri-edit-line text-xs"></i>
                            </button>
                            <button
                              type="button"
                              onClick={() => removeColor(color)}
                              className="ml-1 text-green-600 hover:text-green-800"
                            >
                              <i className="ri-close-line"></i>
                            </button>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  {errors.colors && <p className="text-red-500 text-sm">{errors.colors}</p>}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Pricing */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Giá cả</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Giá sản xuất (VND) *
                    </label>
                    <input
                      type="number"
                      value={formData.productionPrice}
                      onChange={(e) => setFormData({ ...formData, productionPrice: parseInt(e.target.value) || 0 })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.productionPrice ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="0"
                      min="0"
                    />
                    {errors.productionPrice && <p className="text-red-500 text-sm mt-1">{errors.productionPrice}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Giá bán (VND) *
                    </label>
                    <input
                      type="number"
                      value={formData.sellingPrice}
                      onChange={(e) => setFormData({ ...formData, sellingPrice: parseInt(e.target.value) || 0 })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        errors.sellingPrice ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="0"
                      min="0"
                    />
                    {errors.sellingPrice && <p className="text-red-500 text-sm mt-1">{errors.sellingPrice}</p>}
                  </div>

                  {formData.productionPrice > 0 && formData.sellingPrice > 0 && (
                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                      <div className="text-sm text-gray-600">
                        Lợi nhuận: <span className="font-semibold text-green-600">
                          {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' })
                            .format(formData.sellingPrice - formData.productionPrice)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Tỷ suất lợi nhuận: <span className="font-semibold">
                          {((formData.sellingPrice - formData.productionPrice) / formData.productionPrice * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Main Product Images */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ảnh sản phẩm chính *</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Thêm ảnh sản phẩm
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="url"
                        value={newMainImage}
                        onChange={(e) => setNewMainImage(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="https://example.com/image.jpg"
                      />
                      <button
                        type="button"
                        onClick={generateMainImageUrl}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 whitespace-nowrap"
                        disabled={!formData.name.trim()}
                      >
                        Tạo ảnh
                      </button>
                      <button
                        type="button"
                        onClick={addMainImage}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 whitespace-nowrap"
                      >
                        Thêm
                      </button>
                    </div>
                    {errors.image && <p className="text-red-500 text-sm mt-1">{errors.image}</p>}
                  </div>

                  {formData.images.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">Danh sách ảnh ({formData.images.length}):</p>
                      <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto">
                        {formData.images.map((imageUrl, index) => (
                          <div key={index} className="relative border border-gray-200 rounded-lg p-2 bg-white">
                            <img
                              src={imageUrl}
                              alt={`Product ${index + 1}`}
                              className="w-full h-20 object-cover object-top rounded"
                              onError={(e) => {
                                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTUwQzE4Ni4xOSAxNTAgMTc1IDE2MS4xOSAxNzUgMTc1QzE3NSAxODguODEgMTg2LjE5IDIwMCAyMDAgMjAwQzIxMy44MSAyMDAgMjI1IDE4OC40MSAyMjUgMTc1QzIyNSAxNjEuMTkgMjEzLjgxIDE1MCAyMDAgMTUwWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMTI1IDEyNUMxMTguMDk2IDEyNSAxMTIuNSAxMzAuNTk2IDExMi41IDEzNy41VjI2Mi41QzExMi41IDIyOS40MDQgMTE4LjA5NiAyNzUgMTI1IDIzNUgyNzVDMjgxLjkwNCAyNzUgMjg3LjUgMjY5LjQwNCAyODcuNSAyNjIuNVYxMzcuNUMyODcuNSAxMzAuNTk2IDI0MS45MDQgMTI1IDIzNSAxMjVIMTI1WiIgc3Ryb2tlPSIjOUNBM0FGIiBzdHJva2Utd2lkdGg9IjEwIiBmaWxsPSJub25lIi8+Cjwvc3ZnPgo=';
                              }}
                            />
                            <div className="absolute top-1 right-1 flex space-x-1">
                              {formData.image === imageUrl && (
                                <span className="bg-green-500 text-white text-xs px-1 py-0.5 rounded">
                                  Chính
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => setAsMainImage(imageUrl)}
                                className="bg-orange-500 text-white text-xs px-1 py-0.5 rounded hover:bg-orange-600"
                                title="Đặt làm ảnh chính"
                              >
                                <i className="ri-star-line"></i>
                              </button>
                              <button
                                type="button"
                                onClick={() => removeMainImage(imageUrl)}
                                className="bg-red-500 text-white text-xs px-1 py-0.5 rounded hover:bg-red-600"
                                title="Xóa ảnh"
                              >
                                <i className="ri-close-line"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Variants Inventory */}
              {formData.variants.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quản lý theo Size & Màu</h3>
                  
                  <div className="space-y-4 max-h-80 overflow-y-auto">
                    {formData.variants.map((variant, index) => (
                      <div key={`${variant.size}-${variant.color}`} className="border border-gray-200 rounded-lg p-3 bg-white">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm">
                              {variant.size} - {variant.color}
                            </h4>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={variant.quantity}
                              onChange={(e) => updateVariantQuantity(variant.size, variant.color, parseInt(e.target.value) || 0)}
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                              min="0"
                            />
                            <span className="text-xs text-gray-500">cái</span>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Ảnh cho {variant.size} - {variant.color}
                            </label>
                            <div className="flex space-x-2 mb-2">
                              <input
                                type="url"
                                value={selectedVariantForImage?.size === variant.size && selectedVariantForImage?.color === variant.color ? newVariantImage : ''}
                                onChange={(e) => setNewVariantImage(e.target.value)}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="URL ảnh cho variant này"
                                onFocus={() => setSelectedVariantForImage({ size: variant.size, color: variant.color })}
                              />
                              <button
                                type="button"
                                onClick={() => generateVariantImage(variant.size, variant.color)}
                                className="px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 whitespace-nowrap"
                                disabled={!formData.name.trim()}
                              >
                                Tạo
                              </button>
                              <button
                                type="button"
                                onClick={() => addVariantImage(variant.size, variant.color)}
                                className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 whitespace-nowrap"
                                disabled={!newVariantImage.trim() || (selectedVariantForImage?.size !== variant.size || selectedVariantForImage?.color !== variant.color)}
                              >
                                Thêm
                              </button>
                            </div>
                          </div>
                          
                          {variant.images && variant.images.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs text-gray-600">Ảnh đã thêm ({variant.images.length}):</p>
                              <div className="grid grid-cols-4 gap-1">
                                {variant.images.map((imageUrl, imgIndex) => (
                                  <div key={imgIndex} className="relative border border-gray-200 rounded p-1">
                                    <img
                                      src={imageUrl}
                                      alt={`${variant.size} - ${variant.color} ${imgIndex + 1}`}
                                      className="w-full h-12 object-cover object-top rounded"
                                      onError={(e) => {
                                        e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTUwQzE4Ni4xOSAxNTAgMTc1IDE2MS4xOSAxNzUgMTc1QzE3NSAxODguODEgMTg2LjE5IDIwMCAyMDAgMjAwQzIxMy44MSAyMDAgMjI1IDE4OC40MSAyMjUgMTc1QzIyNSAxNjEuMTkgMjEzLjgxIDE1MCAyMDAgMTUwWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMTI1IDEyNUMxMTguMDk2IDEyNSAxMTIuNSAxMzAuNTk2IDExMi41IDEzNy41VjI2Mi41QzExMi41IDIyOS40MDQgMTE4LjA5NiAyNzUgMTI1IDIzNUgyNzVDMjgxLjkwNCAyNzUgMjg3LjUgMjY5LjQwNCAyODcuNSAyNjIuNVYxMzcuNUMyODcuNSAxMzAuNTk2IDI0MS45MDQgMTI1IDIzNSAxMjVIMTI1WiIgc3Ryb2tlPSIjOUNBM0FGIiBzdHJva2Utd2lkdGg9IjEwIiBmaWxsPSJub25lIi8+Cjwvc3ZnPgo=';
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeVariantImage(variant.size, variant.color, imageUrl)}
                                      className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center hover:bg-red-600"
                                      title="Xóa ảnh"
                                    >
                                      <i className="ri-close-line text-xs"></i>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Tổng số lượng:</span>
                      <span className="text-lg font-bold text-orange-600">
                        {formData.variants.reduce((total, v) => total + v.quantity, 0)} cái
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Fixed Footer with Action Buttons */}
      <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg whitespace-nowrap transition-colors font-medium"
          >
            <i className="ri-close-line mr-2"></i>
            Hủy
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-3 bg-orange-600 text-white hover:bg-orange-700 disabled:bg-orange-400 disabled:cursor-not-allowed rounded-lg whitespace-nowrap transition-colors font-medium flex items-center gap-2 shadow-lg"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Đang xử lý...
              </>
            ) : (
              <>
                <i className={product ? "ri-save-line" : "ri-add-line"}></i>
                {product ? 'Cập nhật sản phẩm' : 'Thêm sản phẩm'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
